/**
 * Goals API — Firestore-backed stylist goals + changelog (via FastAPI).
 */
import { apiFetch } from './authApi';
import { trackActionCheckin } from '../analytics/appAnalytics';
import { getApiBaseUrl } from './apiBase';

const API_BASE_URL = getApiBaseUrl();

/**
 * @param {{ store: string, stylist: string }} params
 * @returns {Promise<{ success: boolean, goals: object|null }>}
 */
export async function fetchGoals({ store, stylist }) {
  const params = new URLSearchParams({ store, stylist });
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch goals: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Confirm AI-proposed actions from chat session state → 毎日の行動プラン.
 * Body is session_id + kind only; server reads pending_proposal + store/stylist.
 * kind: 'full' | 'replace' | 'goals' (GOAL_PROPOSAL → KGI / dual focus)
 * @param {{ sessionId: string, kind?: 'full'|'replace'|'goals' }} params
 */
export async function confirmProposedActions({ sessionId, kind = 'full' }) {
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals/actions/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, kind }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to confirm actions: ${response.statusText}`);
  }
  return response.json();
}

/**
 * @param {{ store: string, stylist: string, actions: object[] }} params
 */
export async function submitActions({ store, stylist, actions }) {
  // 行動 never go through PATCH: the server saves them as 確認中 until a manager approves.
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals/actions/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ store, stylist, actions }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to submit actions: ${response.statusText}`);
  }
  return response.json();
}

/**
 * @param {{ store: string, stylist: string, patch: object, changelog?: object }} params
 */
export async function patchGoals({ store, stylist, patch, changelog }) {
  const body = { store, stylist, patch };
  if (changelog) body.changelog = changelog;
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save goals: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Every goal field the backend will accept in a PATCH (`_PATCHABLE_FIELDS` in
 * goals_service.py). Firestore `update` writes null, so the field survives as
 * null rather than being deleted — which is what the FE treats as "unset".
 */
const RESETTABLE_GOAL_FIELDS = [
  'finalSales', 'salesTarget', 'goalDate', 'planLength', 'planStartMonth', 'phaseIndex',
  'focusKey', 'focusTarget', 'subFocusKey', 'subFocusTarget', 'focus',
  'thirdFocusKey', 'thirdFocusTarget', 'subKgiKey', 'subKgiTarget', 'setupStep',
  'planMonths', 'chartTargets', 'verdicts',
  // not 'actions' — the live plan is approval-only now, PATCH ignores it
];

/**
 * Dev reset: null out every goal field for one stylist.
 *
 * Scoped to a single store+stylist because that is what the API exposes — there
 * is no bulk endpoint, so this cannot wipe a whole company by accident.
 *
 * @param {{ store: string, stylist: string }} params
 * @returns {Promise<{ success: boolean, goals: object|null }>}
 */
export async function resetGoals({ store, stylist }) {
  const patch = Object.fromEntries(RESETTABLE_GOAL_FIELDS.map((f) => [f, null]));
  return patchGoals({
    store,
    stylist,
    patch,
    changelog: {
      kind: 'init',
      title: '目標データをリセット',
      summary: '開発用リセットで目標・注力指標・行動プランを消去しました',
      changes: [],
    },
  });
}

/**
 * @param {{ store: string, stylist: string, limit?: number, before?: string }} params
 */
export async function fetchGoalsChangelog({ store, stylist, limit = 50, before = null }) {
  const params = new URLSearchParams({ store, stylist, limit: String(limit) });
  if (before) params.append('before', before);
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals/changelog?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch changelog: ${response.statusText}`);
  }
  return response.json();
}

/**
 * @param {{ store: string, stylist: string, kind: string, icon?: string, title: string, summary?: string, changes: object[], by?: string }} params
 */
export async function postGoalsChangelog({ store, stylist, kind, icon, title, summary, changes, by }) {
  const response = await apiFetch(`${API_BASE_URL}/dashboard/goals/changelog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ store, stylist, kind, icon, title, summary: summary || '', changes, by }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save changelog: ${response.statusText}`);
  }
  return response.json();
}

/**
 * @param {{ store: string, stylist: string, date?: string, start?: string, end?: string, limit?: number }} params
 */
export async function fetchActionCheckins({ store, stylist, date, start, end, limit = 31 }) {
  const params = new URLSearchParams({ store, stylist });
  if (date) params.append('date', date);
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  if (limit) params.append('limit', String(limit));
  const response = await apiFetch(`${API_BASE_URL}/dashboard/action-checkins?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch action check-ins: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Upsert one day of できた/できなかった answers.
 * @param {{ store: string, stylist: string, date?: string, entries: object[], sessionId?: string, source?: string }} params
 */
export async function saveActionCheckin({ store, stylist, date, entries, sessionId, source = 'api' }) {
  const body = { store, stylist, entries, source };
  if (date) body.date = date;
  if (sessionId) body.sessionId = sessionId;
  const response = await apiFetch(`${API_BASE_URL}/dashboard/action-checkins`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save action check-in: ${response.statusText}`);
  }
  trackActionCheckin();
  return response.json();
}

/**
 * シフト（休みの日）: weeklyOff / daysOff / workDays / storeClosed and, with start+end, the off days in range.
 * githubDOCS/TASKS/2026-10-28_休みの日はリマインドを出さない_シフト.md
 * @param {{ store: string, stylist: string, start?: string, end?: string }} params
 */
export async function fetchShift({ store, stylist, start, end }) {
  const params = new URLSearchParams({ store, stylist });
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  const response = await apiFetch(`${API_BASE_URL}/dashboard/shift?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch shift: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Mark one day as 休み (off=true) or back to 出勤 (off=false). Own record or a 管理画面 role.
 * @param {{ store: string, stylist: string, date: string, off?: boolean }} params
 */
export async function setShiftDay({ store, stylist, date, off = true }) {
  const response = await apiFetch(`${API_BASE_URL}/dashboard/shift/day`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ store, stylist, date, off }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save shift: ${response.statusText}`);
  }
  return response.json();
}
